import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import RichText from '@eneco/components/RichText/RichText';
import {
  ComfortBonusCardResponse,
  ComfortBonusLevelDto,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberContactDetails,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { useContent } from '@sitecore/common';
import { DashboardOverviewRendering, GeneralLinkField } from '@sitecore/types/DashboardOverview';

import { StyledDashboardBlock } from './DashboardBlock';
import { DashboardLink } from './DashboardLink';

export const getComfortBonusLevelMapB2C = (fields: DashboardOverviewRendering['fields'], isB2C: boolean = true) => {
  const cardFieldsB2c = fields?.comfortBonusCard;
  return new Map<
    ComfortBonusLevelDto,
    { title: string | undefined; description: string | undefined; link: GeneralLinkField | undefined }
  >([
    [
      'LEVEL0',
      {
        title: cardFieldsB2c?.levelZeroTitle?.value,
        description: cardFieldsB2c?.levelZeroContent?.value,
        link: cardFieldsB2c?.levelZeroLink,
      },
    ],
    [
      'LEVEL1',
      {
        title: cardFieldsB2c?.levelOneTitle?.value,
        description: cardFieldsB2c?.levelOneContent?.value,
        link: cardFieldsB2c?.levelOneLink,
      },
    ],
    [
      'LEVEL2',
      {
        title: cardFieldsB2c?.levelTwoTitle?.value,
        description: cardFieldsB2c?.levelTwoContent?.value,
        link: cardFieldsB2c?.levelTwoLink,
      },
    ],
  ]);
};

export const ComfortBonusCard = ({ card }: { card: ComfortBonusCardResponse }) => {
  const { fields } = useContent<DashboardOverviewRendering>();
  const { data: session } = useSession();
  const {
    selectedAccount: { crmAccountNumber: accountNumber },
  } = useSelfServiceAccount();

  const { data } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberContactDetails,
    {
      path: {
        accountNumber,
      },
    },
    [`/accounts/${accountNumber}/contact/details`],
    session,
  );

  const { customerType } = unwrapData(data) || {};
  const isB2C = customerType === 'Residential';

  const {
    details: { comfortBonusLevel },
  } = card;
  const comfortBonusFieldsB2C = getComfortBonusLevelMapB2C(fields, isB2C).get(comfortBonusLevel);

  if (isB2C && !comfortBonusFieldsB2C?.title) return null;
  if (!isB2C && !fields.comfortBonusCardB2b?.title?.value) return null;

  if (isB2C)
    return (
      <StyledDashboardBlock>
        <RichText html={comfortBonusFieldsB2C?.description} />
        <DashboardLink
          href={comfortBonusFieldsB2C?.link?.value?.href}
          target={comfortBonusFieldsB2C?.link?.value?.target}
          emphasis="high"
          trackingComponentName="comfort_bonus_link"
          trackingComponentType="dashboard_link">
          {comfortBonusFieldsB2C?.link?.value?.text}
        </DashboardLink>
      </StyledDashboardBlock>
    );
  else if (!isB2C) {
    const b2bFields = fields.comfortBonusCardB2b;
    return (
      <StyledDashboardBlock>
        <RichText html={b2bFields?.levelOneContent?.value} />
        {b2bFields?.levelZeroLink?.value?.href && (
          <DashboardLink
            href={b2bFields?.levelZeroLink?.value?.href}
            target={b2bFields?.levelZeroLink?.value?.target}
            emphasis="high"
            trackingComponentName="comfort_bonus_link"
            trackingComponentType="dashboard_link">
            {b2bFields?.levelZeroLink?.value?.text}
          </DashboardLink>
        )}
      </StyledDashboardBlock>
    );
  }
};
